import { Box, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import * as m from 'motion/react-m';

import { tracking } from '#features/analytics';
import { Link } from '#features/routing';
import { Button, Image } from '#features/ui';
import { StoreNote } from '../StoreNote';

type HeroProps = {
  name: string;
  color: string;
  image: string;
  rank?: string;
  role?: string;
  region?: string;
  characterImage?: string;
  characterName?: string;
};

const ACCENT_FALLBACK = '#4B50E6';

const isVisibleColor = (color: string) => {
  const c = color.toLowerCase();
  return c !== '#000000' && c !== '#000' && c !== 'black' && c !== '' && c !== 'transparent';
};

const scrollToHowItWorks = () => {
  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
};

const BoltSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L4.5 13.5a.75.75 0 0 0 .6 1.2H10l-1 7.3 8.5-11.5a.75.75 0 0 0-.6-1.2H12z" />
  </svg>
);

const CheckSvg = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const TagBadge = ({ children }: { children: React.ReactNode }) => (
  <Text
    as="span"
    fontSize="2xs"
    fontWeight="600"
    color="fg.emphasized"
    px={1.5}
    py={0.5}
    borderRadius="md"
    bg="whiteAlpha.100"
    border="1px solid"
    borderColor="whiteAlpha.200"
    whiteSpace="nowrap"
  >
    {children}
  </Text>
);

type FloatChipProps = {
  children: React.ReactNode;
  delay: number;
  duration: number;
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  centerX?: boolean;
};

const FloatChip = ({
  children,
  delay,
  duration,
  top,
  bottom,
  left,
  right,
  centerX,
}: FloatChipProps) => (
  <Flex
    position="absolute"
    top={top}
    bottom={bottom}
    left={centerX ? '50%' : left}
    right={right}
    zIndex={3}
    display={{ base: 'none', md: 'flex' }}
    alignItems="center"
    gap={2.5}
    px={3}
    py={2}
    borderRadius="xl"
    border="1px solid"
    borderColor="whiteAlpha.200"
    background="linear-gradient(165deg, hsla(228,11.6%,16%,0.92) 0%, hsla(228,11.6%,10%,0.85) 100%)"
    style={{
      boxShadow: '0 16px 40px -12px rgba(0,0,0,0.7), 0 0 24px -10px rgba(75,80,230,0.5)',
      backdropFilter: 'blur(8px)',
    }}
    asChild
  >
    <m.div
      initial={{ opacity: 0, scale: 0.85, y: 6, ...(centerX ? { x: '-50%' } : {}) }}
      animate={{ opacity: 1, scale: 1, y: [0, -8, 0], ...(centerX ? { x: '-50%' } : {}) }}
      transition={{
        opacity: { duration: 0.5, delay },
        scale: { duration: 0.5, delay },
        y: { duration, repeat: Infinity, ease: 'easeInOut', delay },
      }}
    >
      {children}
    </m.div>
  </Flex>
);

export const Hero = ({
  name,
  color,
  image,
  rank,
  role,
  region,
  characterImage,
  characterName,
}: HeroProps) => {
  const { t } = useLingui();
  const accentColor = isVisibleColor(color) ? color : ACCENT_FALLBACK;

  // Real game facets when available (game pages), sensible defaults otherwise (index).
  const chipRank = rank ?? 'Diamond II';
  const chipRole = role ?? t`v3.heroChip.role`;
  const chipRegion = region ?? 'EU';

  return (
    <Box position="relative" w="100%">
      {/* Ambient glow bleeding beyond the hero */}
      <Box
        position="absolute"
        top="-220px"
        left="-200px"
        right="-200px"
        bottom="-40px"
        background={`radial-gradient(ellipse 70% 55% at 72% 18%, ${accentColor}22 0%, transparent 68%)`}
        pointerEvents="none"
        zIndex={0}
      />

      <Flex
        alignItems="center"
        flexDir={{ base: 'column', lg: 'row' }}
        w="100%"
        gap={{ base: 2, lg: 6 }}
        position="relative"
        zIndex={1}
      >
        {/* Copy */}
        <Stack
          gap={{ base: 5, lg: 6 }}
          w={{ base: '100%', lg: '54%' }}
          flex="1"
          order={{ base: 2, lg: 1 }}
          alignItems={{ base: 'center', lg: 'flex-start' }}
          textAlign={{ base: 'center', lg: 'left' }}
          mt={{ base: 4, lg: 0 }}
        >
          <Heading
            as="h1"
            fontWeight="900"
            fontSize={{ base: '5xl', md: '6xl', xl: '7xl' }}
            lineHeight="0.92"
            letterSpacing="-0.03em"
            textTransform="uppercase"
          >
            {/* On a game page the H1 leads with "<Game> LFG" so the exact
                high-volume search phrase (e.g. "Valorant LFG") lives in the H1;
                the index keeps the brand headline. */}
            <Text as="span" display="block">
              {name ? `${name} LFG` : t`v3.hero.title1`}
            </Text>
            <Text as="span" display="block" color={accentColor}>
              {name ? t`v3.hero.title1` : t`v3.hero.title2`}
            </Text>
          </Heading>

          <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.7" color="fg.muted" maxW="500px">
            {/* Game pages lead the subtitle with "Find <Game> teammates" so the
                keyword phrase also sits high in the copy; index keeps the generic
                line. Inline macro interpolation binds the value on SSR + client. */}
            {name
              ? t`Find ${name} teammates by rank, platform and schedule. Post once or browse live LFG - your next squad is minutes away.`
              : t`v3.hero.subtitle`}
          </Text>

          <Flex
            gap={3}
            flexDir={{ base: 'column', sm: 'row' }}
            w={{ base: '100%', sm: 'auto' }}
            mt={1}
          >
            <Button
              minW="210px"
              w={{ base: '100%', sm: 'auto' }}
              size="lg"
              style={{ boxShadow: `0 0 26px ${accentColor}55` }}
              onClick={() => tracking.event({ action: 'click_get_started' })}
              asChild
            >
              <Link to="/{-$locale}/sign-up">{t`v3.hero.ctaPrimary`}</Link>
            </Button>
            <Button
              variant="outline"
              size="lg"
              color="fg.emphasized"
              borderColor="whiteAlpha.200"
              onClick={scrollToHowItWorks}
              _hover={{ borderColor: 'whiteAlpha.400', bg: 'whiteAlpha.50' }}
            >
              {t`v3.hero.ctaSecondary`}
            </Button>
          </Flex>

          <StoreNote />
        </Stack>

        {/* Character */}
        <Box
          w={{ base: '100%', lg: '46%' }}
          maxW={{ base: '420px', lg: '560px' }}
          height={{ base: '340px', sm: '440px', xl: '540px' }}
          position="relative"
          order={{ base: 1, lg: 2 }}
        >
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="115%"
            h="115%"
            background={`radial-gradient(circle at center, ${accentColor}22 0%, ${accentColor}08 42%, transparent 70%)`}
            zIndex={0}
            pointerEvents="none"
          />
          {image && (
            <Image
              key={image}
              src={image}
              sizes="(min-width: 1024px) 560px, 90vw"
              alt={name ? `${name} - Teamplay.gg` : 'Teamplay.gg'}
              loading="eager"
              fetchPriority="high"
              style={{
                objectFit: 'contain',
                height: '100%',
                width: '100%',
                position: 'relative',
                zIndex: 1,
                // Game hero images are rectangular photos with a hard bottom edge.
                // Fade the lower ~16% into transparency so the character dissolves
                // into the page background instead of reading as a hard crop -
                // works for any game regardless of source composition.
                maskImage: 'linear-gradient(to bottom, #000 84%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to bottom, #000 84%, transparent 100%)',
              }}
            />
          )}

          {/* Floating product chips - depth + ties the hero to the product */}
          {/* Precision: matched by rank, role, region. Anchored over the image's
              bottom edge (centered) so the photo tucks behind the card instead of
              reading as a hard crop. */}
          <FloatChip bottom="4%" centerX delay={0.6} duration={4.5}>
            <Stack gap={2}>
              <Flex alignItems="center" gap={1.5}>
                {characterImage ? (
                  <Box
                    w="18px"
                    h="18px"
                    borderRadius="full"
                    overflow="hidden"
                    flexShrink={0}
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                  >
                    <Image
                      src={characterImage}
                      sizes="18px"
                      alt={characterName ?? ''}
                      loading="eager"
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                    />
                  </Box>
                ) : (
                  <Flex
                    w="16px"
                    h="16px"
                    borderRadius="full"
                    alignItems="center"
                    justifyContent="center"
                    bg="#22c55e"
                    color="white"
                    flexShrink={0}
                    style={{ boxShadow: '0 0 8px rgba(34,197,94,0.6)' }}
                  >
                    <CheckSvg />
                  </Flex>
                )}
                <Text fontSize="xs" fontWeight="700" color="fg.emphasized" lineHeight="1.1">
                  {t`v3.heroChip.matchLabel`}
                </Text>
              </Flex>
              <Flex gap={1.5}>
                <TagBadge>{chipRank}</TagBadge>
                <TagBadge>{chipRole}</TagBadge>
                <TagBadge>{chipRegion}</TagBadge>
              </Flex>
            </Stack>
          </FloatChip>

          {/* Speed: your next squad is minutes away */}
          <FloatChip top="12%" right="-2%" delay={1.1} duration={5.2}>
            <Flex
              w="30px"
              h="30px"
              borderRadius="lg"
              alignItems="center"
              justifyContent="center"
              color="white"
              flexShrink={0}
              style={{ background: `linear-gradient(135deg, ${accentColor} 0%, #8E7BFF 100%)` }}
            >
              <BoltSvg />
            </Flex>
            <Box>
              <Text fontSize="xs" fontWeight="700" color="fg.emphasized" lineHeight="1.1">
                {t`v3.heroChip.matchFound`}
              </Text>
              <Text fontSize="2xs" color="fg.muted" lineHeight="1.2">
                {t`v3.heroChip.matchTime`}
              </Text>
              <Box
                mt={1.5}
                h="3px"
                w="88px"
                borderRadius="full"
                bg="whiteAlpha.200"
                overflow="hidden"
              >
                <m.div
                  initial={{ width: '0%' }}
                  animate={{ width: ['0%', '100%', '100%', '0%'], opacity: [1, 1, 1, 0] }}
                  transition={{
                    duration: 2.6,
                    times: [0, 0.55, 0.82, 1],
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 1.1,
                  }}
                  style={{
                    height: '100%',
                    borderRadius: '9999px',
                    background: `linear-gradient(90deg, ${accentColor}, #8E7BFF)`,
                  }}
                />
              </Box>
            </Box>
          </FloatChip>
        </Box>
      </Flex>
    </Box>
  );
};
